import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import { downloadTankDesignReportPdf } from '../../services/designService';
import { downloadBlob } from '../../utils/downloadFile';
import ScrollX from '../../components/ScrollX';

const TANK_REPORT_CALCULATOR_TYPES = new Set(['tank_design', 'tank_sizing']);

const initialReportMeta = {
  document_no: '',
  revision: '0',
  report_date: '',
  project_name: '',
  client_name: '',
  location: '',
  consultant_name: '',
  prepared_by: '',
  prepared_by_designation: '',
  checked_by: '',
  checked_by_designation: '',
  approved_by: '',
  approved_by_designation: '',
};

const statusColors = {
  draft: 'bg-gray-200 text-gray-800',
  pending_review: 'bg-amber-100 text-amber-800',
  engineer_approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

// Generic label formatting so this page works for whichever calculator_type
// the record is (only pipe_pressure_drop exists today, more are planned) -
// snake_case -> Title Case, no per-field config needed.
const formatLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isPlainValue = (v) => v === null || typeof v !== 'object';

const KeyValueGrid = ({ data }) => {
  const entries = Object.entries(data || {}).filter(([, v]) => isPlainValue(v));
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
      {entries.map(([key, value]) => (
        <div key={key}>
          <div className="text-xs text-gray-500">{formatLabel(key)}</div>
          <div className="font-semibold text-gray-900">{value === null || value === '' ? '—' : String(value)}</div>
        </div>
      ))}
    </div>
  );
};

// warnings/assumptions/calculation_steps are rendered as their own
// dedicated sections (see below) - excluded here even when they show up
// again inside a nested sub-object (e.g. outputs.tank.warnings), since a
// calculator that composes others (like tank_design over tank_sizing +
// jacket_design) already merges all of those into the top-level lists.
const SPECIAL_KEYS = new Set(['warnings', 'assumptions', 'calculation_steps']);

// Recursively renders a plain-value grid for `data`, then a titled
// sub-section for each nested object field (one level of nesting is the
// common case - e.g. outputs.tank, outputs.jacket, outputs.jacket.inner_wall_check -
// but this recurses arbitrarily deep so it keeps working for whatever
// shape a future composed calculator produces, with no per-calculator
// config needed here.
const OutputSection = ({ title, data }) => {
  const allEntries = Object.entries(data || {}).filter(([k]) => !SPECIAL_KEYS.has(k));
  const plainEntries = allEntries.filter(([, v]) => isPlainValue(v));
  const nestedEntries = allEntries.filter(([, v]) => v !== null && typeof v === 'object' && !Array.isArray(v));
  if (plainEntries.length === 0 && nestedEntries.length === 0) return null;
  return (
    <div>
      {title && <h4 className="text-xs font-semibold text-gray-700 mb-2">{formatLabel(title)}</h4>}
      <KeyValueGrid data={Object.fromEntries(plainEntries)} />
      {nestedEntries.map(([key, value]) => (
        <div key={key} className="mt-3 pl-3 border-l-2 border-gray-100">
          <OutputSection title={key} data={value} />
        </div>
      ))}
    </div>
  );
};

const DesignCalculationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedCalculation, isLoading, error, fetchCalculation, reviewCalculation, isSubmitting } = useDesignStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reportMeta, setReportMeta] = useState(initialReportMeta);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const handleReportMetaChange = (field) => (e) => setReportMeta((m) => ({ ...m, [field]: e.target.value }));

  useEffect(() => {
    fetchCalculation(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading || !selectedCalculation) {
    return (
      <div className="min-h-screen p-6">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  const calc = selectedCalculation;
  const outputs = calc.calculation_outputs || {};
  const canReview = calc.status === 'draft' || calc.status === 'pending_review';

  const handleReview = async (decision) => {
    if (decision === 'rejected' && !reviewNotes.trim()) {
      showToast('A reason is required to reject a calculation', 'error');
      return;
    }
    const result = await reviewCalculation(calc.id, decision, user?.id || null, reviewNotes || null);
    if (result.success) {
      showToast(`Calculation ${decision === 'engineer_approved' ? 'approved' : 'rejected'}`, 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const canGenerateReport = TANK_REPORT_CALCULATOR_TYPES.has(calc.calculator_type);

  const handleGenerateReport = async () => {
    if (!reportMeta.document_no.trim()) {
      showToast('Document No. is required to generate the report', 'error');
      return;
    }
    setIsGeneratingReport(true);
    try {
      const res = await downloadTankDesignReportPdf(calc.id, reportMeta);
      downloadBlob(res.data, `tank_design_report_${calc.id}_${reportMeta.document_no.replace(/[/\s]/g, '_')}.pdf`);
      showToast('Report downloaded', 'success');
    } catch (err) {
      showToast('Failed to generate report', 'error');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/design/calculations" className="text-xs text-white hover:underline">
          ← Back to Saved Calculations
        </Link>

        <div className="flex items-center justify-between mt-2 mb-6 flex-wrap gap-3">
          <h1 className="text-xl font-bold text-white capitalize">
            {calc.calculator_type.replace(/_/g, ' ')} #{calc.id}
          </h1>
          <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${statusColors[calc.status]}`}>
            {calc.status.replace(/_/g, ' ')}
          </span>
        </div>

        <div className="glass-card p-6 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-gray-500">Project</div>
              <div className="font-semibold text-gray-900">{calc.project_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Created By</div>
              <div className="font-semibold text-gray-900">{calc.created_by_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Saved</div>
              <div className="font-semibold text-gray-900">
                {calc.created_at ? new Date(calc.created_at).toLocaleString() : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Formula Version</div>
              <div className="font-semibold text-gray-900">{calc.formula_version}</div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Inputs</h2>
          <KeyValueGrid data={calc.client_data} />
          {calc.client_data?.fittings?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-gray-500 mb-1">Fittings</div>
              <ul className="text-sm text-gray-900 list-disc list-inside">
                {calc.client_data.fittings.map((f, i) => (
                  <li key={i}>{formatLabel(f.type)} × {f.quantity}{f.k_value_override != null ? ` (K override: ${f.k_value_override})` : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="glass-card p-6 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Results</h2>

          {outputs.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
              {outputs.warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
              ))}
            </div>
          )}

          <OutputSection data={outputs} />

          {outputs.calculation_steps?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Calculation Steps</h3>
              <div className="bg-gray-50 rounded border border-gray-100">
                <ScrollX>
                <table className="w-full text-xs">
                  <tbody>
                    {outputs.calculation_steps.map((step, i) => {
                      const isSectionHeader = step.formula === '' && step.result === '';
                      return (
                        <tr key={i} className={isSectionHeader ? 'bg-gray-100' : 'border-b border-gray-100 last:border-0'}>
                          <td className={`px-3 py-2 whitespace-nowrap ${isSectionHeader ? 'font-bold text-gray-500' : 'font-medium text-gray-700'}`} colSpan={isSectionHeader ? 3 : 1}>
                            {step.step}
                          </td>
                          {!isSectionHeader && (
                            <>
                              <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                              <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </ScrollX>
              </div>
            </div>
          )}

          {outputs.assumptions?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Assumptions</h3>
              <ul className="list-disc list-inside space-y-1">
                {outputs.assumptions.map((a, i) => (
                  <li key={i} className="text-xs text-gray-600">{a}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {canGenerateReport && (
          <div className="glass-card p-6 mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Generate ASME Design Report</h2>
            <p className="text-xs text-gray-500 mb-4">
              Formal, consultant-facing PDF: company letterhead + watermark, full step-by-step calculation working
              with ASME clause references, a thickness adequacy PASS/FAIL summary, assumptions/limitations, and
              Prepared/Checked/Approved sign-off. This metadata is used only for this PDF — it isn't saved.
            </p>
            {calc.status !== 'engineer_approved' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                ⚠ This calculation is not yet engineer-approved — the report will carry a preliminary/draft banner.
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Document No. *</label>
                <input required type="text" value={reportMeta.document_no} onChange={handleReportMetaChange('document_no')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="e.g. KWT-TR-2026-001" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Revision</label>
                <input type="text" value={reportMeta.revision} onChange={handleReportMetaChange('revision')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Report Date</label>
                <input type="text" value={reportMeta.report_date} onChange={handleReportMetaChange('report_date')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = today" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Name</label>
                <input type="text" value={reportMeta.project_name} onChange={handleReportMetaChange('project_name')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder={calc.project_name || 'Blank = linked project'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Name</label>
                <input type="text" value={reportMeta.client_name} onChange={handleReportMetaChange('client_name')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = linked project's client" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <input type="text" value={reportMeta.location} onChange={handleReportMetaChange('location')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = linked project's location" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Consulting Engineer (optional)</label>
                <input type="text" value={reportMeta.consultant_name} onChange={handleReportMetaChange('consultant_name')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            <p className="text-xs font-medium text-gray-700 mb-2">Prepared / Checked / Approved</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Prepared By</label>
                <input type="text" value={reportMeta.prepared_by} onChange={handleReportMetaChange('prepared_by')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2" placeholder="Name" />
                <input type="text" value={reportMeta.prepared_by_designation} onChange={handleReportMetaChange('prepared_by_designation')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Designation" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Checked By</label>
                <input type="text" value={reportMeta.checked_by} onChange={handleReportMetaChange('checked_by')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2" placeholder="Name" />
                <input type="text" value={reportMeta.checked_by_designation} onChange={handleReportMetaChange('checked_by_designation')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Designation" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Approved By</label>
                <input type="text" value={reportMeta.approved_by} onChange={handleReportMetaChange('approved_by')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2" placeholder="Name" />
                <input type="text" value={reportMeta.approved_by_designation} onChange={handleReportMetaChange('approved_by_designation')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Designation" />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isGeneratingReport ? 'Generating...' : 'Download PDF Report'}
              </button>
            </div>
          </div>
        )}

        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Engineer Review</h2>

          {canReview ? (
            <>
              <textarea
                rows={3}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Review notes (required to reject, optional to approve)"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/design/calculations')}
                  className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleReview('rejected')}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-60"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => handleReview('engineer_approved')}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
                >
                  {isSubmitting ? 'Saving...' : 'Approve'}
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm space-y-1">
              <p>
                <span className="text-gray-500">Reviewed by:</span>{' '}
                <span className="font-semibold text-gray-900">{calc.reviewed_by_name || '—'}</span>
              </p>
              <p>
                <span className="text-gray-500">Reviewed at:</span>{' '}
                <span className="font-semibold text-gray-900">
                  {calc.reviewed_at ? new Date(calc.reviewed_at).toLocaleString() : '—'}
                </span>
              </p>
              {calc.review_notes && (
                <p>
                  <span className="text-gray-500">Notes:</span>{' '}
                  <span className="text-gray-900">{calc.review_notes}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignCalculationDetailPage;
